// Phase 13 M5 — Public blockchain anchor verification.
//
// `GET /api/v1/blockchain/anchor/:date` exposes a single `MerkleAnchor` row
// as JSON, so anyone (consumer scanning a carnicería QR, auditor, partner)
// can independently check the day-summary root that was anchored on chain.
// The endpoint is anonymous (no auth) — the data is non-sensitive and the
// whole point is third-party verifiability.
//
// `:date` is a calendar day in `YYYY-MM-DD`. The matching `MerkleAnchor.date`
// row is returned with the on-chain tx hash (when the daily anchor job
// already succeeded) plus a ready-to-click polygonscan URL so the QR page
// footer can deep-link without re-hardcoding the explorer host.
import { z } from 'zod';
/** `YYYY-MM-DD`. Lower bound is arbitrary but stops obvious garbage paths. */
export const AnchorDateParamSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
});
export const AnchorVerificationSchema = z.object({
    /** `YYYY-MM-DD`. Echo of the requested calendar day. */
    date: z.string(),
    /** Hex Merkle root of every event hashed on that day. */
    merkle_root: z.string(),
    /** Number of events folded into the root. */
    events_count: z.number().int().nonnegative(),
    /** On-chain transaction hash. `null` when the anchor job persisted the
     *  off-chain row but the Polygon write hasn't succeeded yet. */
    polygon_tx_hash: z.string().nullable(),
    /** Deep link to the public explorer when `polygon_tx_hash` is present. */
    polygonscan_url: z.string().url().nullable(),
});
/** Explorer host. Matches the URL hard-coded in the QR template footer so
 *  both QR HTML + the JSON endpoint resolve to the same explorer. */
export const POLYGONSCAN_TX_BASE_URL = 'https://amoy.polygonscan.com/tx/';
export function buildPolygonscanUrl(txHash) {
    if (!txHash)
        return null;
    return `${POLYGONSCAN_TX_BASE_URL}${txHash}`;
}
//# sourceMappingURL=blockchain.js.map